type StorageReader = Pick<Storage, "getItem">;
type StorageWriter = Pick<Storage, "setItem" | "removeItem">;

const defaultStorage = (): Storage | undefined => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
};

export const tabOrderStorageKey = (machineId: string, projectId: string): string =>
  `racler.tabs.v1:${machineId}:${projectId}`;

/**
 * Safely loads persisted open tab order for a specific Project.
 * Stale or corrupt stored state is safely ignored and falls back to [].
 */
export function loadTabOrder(
  machineId: string,
  projectId: string,
  storage: StorageReader | undefined = defaultStorage(),
): string[] {
  if (!storage || !machineId || !projectId) return [];
  try {
    const raw = storage.getItem(tabOrderStorageKey(machineId, projectId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const item of parsed) {
      if (typeof item === "string" && item.trim().length > 0 && !seen.has(item)) {
        seen.add(item);
        result.push(item);
      }
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * Persists open tab order for a specific Project.
 * When no tabs remain open, the stored entry is removed.
 */
export function saveTabOrder(
  machineId: string,
  projectId: string,
  paths: string[],
  storage: StorageWriter | undefined = defaultStorage(),
): void {
  if (!storage || !machineId || !projectId) return;
  try {
    const key = tabOrderStorageKey(machineId, projectId);
    const valid = paths.filter((p) => typeof p === "string" && p.trim().length > 0);
    if (valid.length === 0) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(valid));
    }
  } catch {
    // Ignore storage quota or access errors
  }
}

/**
 * Pure helper: moves an element in an array from fromIndex to toIndex.
 */
export function reorderTabPaths<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (
    fromIndex < 0 ||
    fromIndex >= items.length ||
    toIndex < 0 ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  if (moved !== undefined) {
    next.splice(toIndex, 0, moved);
  }
  return next;
}

/**
 * Updates persisted paths when a file or folder is renamed/moved.
 */
export function updateRenamedTabOrder(paths: string[], from: string, to: string): string[] {
  return paths.map((p) => (p === from ? to : p.startsWith(`${from}/`) ? to + p.slice(from.length) : p));
}
